import { useState, useEffect, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { poweredByUrl } from "../components/PoweredBy";
import {
  Calendar,
  Clock,
  Users,
  Loader2,
  AlertCircle,
  CheckCircle,
  XCircle,
  MapPin,
  Phone,
  CalendarPlus,
  PartyPopper,
  Info,
  Edit3,
  Minus,
  Plus,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  UtensilsCrossed,
} from "lucide-react";
import AppDownloadCta from "../components/AppDownloadCta";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { useT } from "../i18n";
import {
  format,
  addMonths,
  subMonths,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameDay,
  isToday,
  isBefore,
  startOfDay,
} from "date-fns";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:4000/api";

// Solicitud de cambio pendiente de revisión por el restaurante. El backend la
// guarda en `Reservation.pendingChangeRequest` (JSON) con este shape — ver
// `reservamanager-backend/src/modules/public/controller.ts` (updateReservationByCode).
interface PendingChangeRequest {
  date?: string;
  time?: string;
  partySize?: number;
  requestedAt?: string;
  oldValues?: {
    date: string;
    time: string;
    partySize: number;
  };
  // Textos ya formateados por el backend en español, p.ej.
  // "fecha: 10/07/2026 → 15/07/2026", "hora: 20:00 → 21:00".
  changes?: string[];
}

interface ReservationData {
  id: string;
  confirmationCode: string;
  date: string;
  time: string;
  partySize: number;
  status: string;
  notes: string | null;
  // Nombre del salón asignado a la mesa de la reserva (si la reserva tiene
  // mesa y esta pertenece a un salón). El backend solo lo informa cuando
  // aplica; en el resto de casos llega `null`/`undefined` y no se pinta nada.
  roomName?: string | null;
  cancelledBy?: string | null;
  cancellationReason?: string | null;
  cancelledAt?: string | null;
  pendingChangeRequest?: PendingChangeRequest | null;
  pendingChangeRequestedAt?: string | null;
  customer: {
    firstName: string;
    lastName: string | null;
  };
  location: {
    // Slug de la sede: lo necesitamos para pedir los huecos REALES a
    // `GET /public/organizations/:slug/slots|calendar` antes de dejar pedir
    // un cambio (ver `.claude/memorias/frontend.notas.md`, 18-sep-2026).
    slug: string;
    name: string;
    address: string | null;
    phone: string | null;
    logo: string | null;
  };
}

// Hueco real de un día concreto, tal como lo devuelve
// `GET /public/organizations/:slug/slots` (mismo endpoint que usa `App.tsx`
// para la reserva nueva).
interface PublicTimeSlot {
  time: string;
  available: boolean;
}

// Resumen de un día del mes para el calendario, tal como lo devuelve
// `GET /public/organizations/:slug/calendar` (mismo shape que `App.tsx`,
// recortado a los campos que usa esta pantalla).
interface DayAvailabilityInfo {
  date: string;
  isOpen: boolean;
  reason?: string;
}

// Menús por fechas: selección ya guardada de la reserva + catálogo de menús
// disponibles para esa fecha concreta (ambos vienen de
// `GET /public/reservations/:code/menus`). `editable`/`editableUntil` los
// decide el backend (normalmente: hasta el día anterior a la reserva).
interface MenuOption {
  id: string;
  name: string;
  description: string | null;
  price: number;
  scope: "per_guest" | "all_guests";
  // El backend ya filtra `availableMenus` por los comensales de la reserva;
  // este campo es solo informativo, no se usa para filtrar en cliente.
  minPartySize?: number;
}

interface MenuSelection {
  menuId: string;
  name: string;
  quantity: number;
  price: number;
  scope: "per_guest" | "all_guests";
}

interface ReservationMenusData {
  selections: MenuSelection[];
  availableMenus: MenuOption[];
  editable: boolean;
  editableUntil: string | null;
}

type TFn = ReturnType<typeof useT>["t"];
type FormatDateFn = ReturnType<typeof useT>["formatDate"];
type TPeopleFn = ReturnType<typeof useT>["tPeople"];

// Resume en un texto legible la solicitud de cambio pendiente. Prioriza los
// textos "campo: antes → después" que ya manda el backend en `changes` (texto
// libre en español, se muestra tal cual — ver `.claude/memorias/frontend.notas.md`);
// si por lo que sea no vinieran (respuesta antigua/parcial), se compone un
// resumen más simple a partir de los valores nuevos sueltos, ya traducido.
const formatPendingChangeSummary = (
  pending: PendingChangeRequest,
  t: TFn,
  formatDate: FormatDateFn,
): string => {
  if (pending.changes && pending.changes.length > 0) {
    return pending.changes.join(", ");
  }
  const parts: string[] = [];
  if (pending.date) {
    parts.push(
      t("managePendingSummaryDate", {
        date: formatDate(new Date(pending.date), "dayMonth"),
      }),
    );
  }
  if (pending.time) {
    parts.push(t("managePendingSummaryTime", { time: pending.time }));
  }
  if (typeof pending.partySize === "number") {
    parts.push(t("managePendingSummaryPeople", { n: pending.partySize }));
  }
  return parts.join(", ");
};

const getStatusConfig = (
  t: TFn,
): Record<string, { label: string; color: string; bgColor: string; icon: string }> => ({
  pending: {
    label: t("statusPending"),
    color: "text-yellow-700",
    bgColor: "bg-yellow-100",
    icon: "clock",
  },
  confirmed: {
    label: t("statusConfirmed"),
    color: "text-blue-700",
    bgColor: "bg-blue-100",
    icon: "check",
  },
  double_confirmed: {
    label: t("statusConfirmed"),
    color: "text-green-700",
    bgColor: "bg-green-100",
    icon: "check-double",
  },
  cancelled: {
    label: t("statusCancelled"),
    color: "text-red-700",
    bgColor: "bg-red-100",
    icon: "x",
  },
  completed: {
    label: t("statusCompleted"),
    color: "text-emerald-700",
    bgColor: "bg-emerald-100",
    icon: "check",
  },
  no_show: {
    label: t("statusNoShow"),
    color: "text-gray-700",
    bgColor: "bg-gray-100",
    icon: "x",
  },
});

// Función para generar enlace de Google Calendar
const generateGoogleCalendarUrl = (
  reservation: ReservationData,
  t: TFn,
  tPeople: TPeopleFn,
): string => {
  const startDate = new Date(reservation.date);
  const [hours, minutes] = reservation.time.split(":").map(Number);
  startDate.setHours(hours, minutes, 0, 0);

  const endDate = new Date(startDate);
  endDate.setHours(endDate.getHours() + 2); // Duración estimada de 2 horas

  const formatDateForGoogle = (date: Date) => {
    return date.toISOString().replace(/-|:|\.\d{3}/g, "");
  };

  const detailsLines = [
    t("manageGoogleDetailsIntro", { people: tPeople(reservation.partySize) }),
    t("manageGoogleDetailsCode", { code: reservation.confirmationCode }),
    ...(reservation.notes
      ? [t("manageGoogleDetailsNotes", { notes: reservation.notes })]
      : []),
  ];

  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: t("manageGoogleEventTitle", { name: reservation.location.name }),
    dates: `${formatDateForGoogle(startDate)}/${formatDateForGoogle(endDate)}`,
    details: detailsLines.join("\n"),
    location: reservation.location.address || reservation.location.name,
  });

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

export default function ReservationManage() {
  const { token } = useParams<{ token: string }>();
  // Token opaco de acceso a las notas (alergias) de la reserva, emitido por el
  // backend solo a quien acaba de crearla o recibe el email/WhatsApp con el
  // enlace. Sin él (o si es inválido/caducado) el backend simplemente devuelve
  // `notes: null` en las cuatro rutas — nunca 401/403 — así que hay que
  // reenviarlo tal cual en TODAS las llamadas por código, no solo en la carga
  // inicial, o las notas desaparecerían en cuanto el cliente confirme/cancele/
  // modifique (ver `.claude/memorias/frontend.notas.md`).
  const [searchParams] = useSearchParams();
  const notesToken = searchParams.get("t");
  const q = notesToken ? `?t=${encodeURIComponent(notesToken)}` : "";
  const { t, tPeople, tSplit, formatDate, formatTime, weekdays } = useT();
  const [loading, setLoading] = useState(true);
  const [reservation, setReservation] = useState<ReservationData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<
    "confirm" | "cancel" | null
  >(null);
  const [actionSuccess, setActionSuccess] = useState<
    "confirm" | "cancel" | "modify" | null
  >(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  // Edit form state
  const [editDate, setEditDate] = useState<Date | null>(null);
  const [editTime, setEditTime] = useState<string>("");
  const [editPartySize, setEditPartySize] = useState<number>(2);
  const [editLoading, setEditLoading] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  // Huecos reales del día elegido (nunca una lista fija, ver
  // `.claude/memorias/frontend.notas.md`, 18-sep-2026).
  const [availableSlots, setAvailableSlots] = useState<PublicTimeSlot[]>([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  // Motivo de cierre del día elegido (contenido del propio restaurante, se
  // pinta tal cual). `null` = día abierto pero sin huecos para la ocupación
  // pedida, o aún no se ha elegido/cargado nada.
  const [slotsClosedReason, setSlotsClosedReason] = useState<string | null>(
    null,
  );
  // Disponibilidad por día del mes visible en el calendario del modal
  // (`GET /public/organizations/:slug/calendar`), acumulada por si el
  // cliente navega entre meses dentro de la misma sesión del modal.
  const [calendarAvailability, setCalendarAvailability] = useState<
    Record<string, DayAvailabilityInfo>
  >({});
  // Lock síncrono anti-doble-envío: `editLoading` (estado React) no bloquea un
  // segundo tap dentro del mismo ciclo de render (el `disabled` del botón solo
  // surte efecto tras el re-render). Este ref sí es síncrono.
  const isSubmittingRef = useRef(false);

  // Menús por fechas: petición aparte de la reserva, en paralelo y silenciosa
  // — si falla o el negocio no tiene menús, `menusData` queda `null` y la
  // sección "Tu menú" simplemente no aparece (mismo criterio que "sin
  // selections NI availableMenus").
  const [menusData, setMenusData] = useState<ReservationMenusData | null>(
    null,
  );
  const [showMenuModal, setShowMenuModal] = useState(false);
  const [menuModalQuantities, setMenuModalQuantities] = useState<
    Record<string, number>
  >({});
  const [menuModalExpandedId, setMenuModalExpandedId] = useState<
    string | null
  >(null);
  const [menuModalSaving, setMenuModalSaving] = useState(false);
  const [menuModalError, setMenuModalError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    const loadMenus = async () => {
      try {
        const res = await fetch(`${API_URL}/public/reservations/${token}/menus`);
        const data = await res.json();
        if (res.ok && data.success) {
          setMenusData({
            selections: Array.isArray(data.selections) ? data.selections : [],
            availableMenus: Array.isArray(data.availableMenus)
              ? data.availableMenus
              : [],
            editable: !!data.editable,
            editableUntil: data.editableUntil || null,
          });
        }
      } catch {
        // Silencioso a propósito: sin menús cargados, la sección "Tu menú"
        // no aparece, igual que un negocio sin la feature contratada.
      }
    };
    loadMenus();
  }, [token]);

  const menuModalTotal = Object.values(menuModalQuantities).reduce(
    (sum, q) => sum + q,
    0,
  );
  const menuModalExceedsPartySize = reservation
    ? menuModalTotal > reservation.partySize
    : false;

  const openMenuModal = () => {
    if (!menusData) return;
    const initial: Record<string, number> = {};
    menusData.selections.forEach((sel) => {
      initial[sel.menuId] = sel.quantity;
    });
    setMenuModalQuantities(initial);
    setMenuModalExpandedId(null);
    setMenuModalError(null);
    setShowMenuModal(true);
  };

  const incrementMenuModalQuantity = (menuId: string) => {
    if (!reservation) return;
    setMenuModalQuantities((prev) => {
      const total = Object.values(prev).reduce((sum, q) => sum + q, 0);
      if (total >= reservation.partySize) return prev;
      return { ...prev, [menuId]: (prev[menuId] || 0) + 1 };
    });
  };

  const decrementMenuModalQuantity = (menuId: string) => {
    setMenuModalQuantities((prev) => {
      const current = prev[menuId] || 0;
      if (current <= 0) return prev;
      return { ...prev, [menuId]: current - 1 };
    });
  };

  const setAllGuestsMenuModalQuantity = (menuId: string, checked: boolean) => {
    if (!reservation) return;
    setMenuModalQuantities((prev) => ({
      ...prev,
      [menuId]: checked ? reservation.partySize : 0,
    }));
  };

  const handleSaveMenu = async () => {
    if (!token || !menusData) return;
    setMenuModalSaving(true);
    setMenuModalError(null);
    try {
      const selections = Object.entries(menuModalQuantities)
        .filter(([, quantity]) => quantity > 0)
        .map(([menuId, quantity]) => ({ menuId, quantity }));
      const res = await fetch(
        `${API_URL}/public/reservations/${token}/menus${q}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ selections }),
        },
      );
      const data = await res.json();
      if (res.ok) {
        // Se recompone `selections` a partir del catálogo local en vez de
        // depender del shape exacto de la respuesta del PUT (el contrato
        // solo garantiza 200 OK / 422-400 con mensaje).
        const updatedSelections: MenuSelection[] = menusData.availableMenus
          .filter((menu) => (menuModalQuantities[menu.id] || 0) > 0)
          .map((menu) => ({
            menuId: menu.id,
            name: menu.name,
            quantity: menuModalQuantities[menu.id],
            price: menu.price,
            scope: menu.scope,
          }));
        setMenusData((prev) =>
          prev ? { ...prev, selections: updatedSelections } : prev,
        );
        setShowMenuModal(false);
      } else {
        setMenuModalError(data.message || t("manageMenuSaveErrorFallback"));
      }
    } catch {
      setMenuModalError(t("commonConnectionErrorShort"));
    } finally {
      setMenuModalSaving(false);
    }
  };

  useEffect(() => {
    const fetchReservation = async () => {
      try {
        const res = await fetch(
          `${API_URL}/public/reservations/${token}${q}`,
        );
        const data = await res.json();

        if (!res.ok) {
          setError(data.message || t("manageFetchErrorFallback"));
          return;
        }

        setReservation(data.reservation);
      } catch (err) {
        setError(t("commonConnectionErrorLater"));
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchReservation();
    }
  }, [token, q]);

  const handleConfirm = async () => {
    if (!token) return;
    setShowConfirmModal(false);
    setActionLoading("confirm");
    setError(null);
    try {
      const res = await fetch(
        `${API_URL}/public/reservations/${token}/confirm${q}`,
        {
          method: "POST",
        },
      );
      const data = await res.json();

      if (res.ok) {
        setReservation(data.reservation);
        setActionSuccess("confirm");
      } else {
        setError(data.message || t("manageConfirmErrorFallback"));
      }
    } catch (err) {
      setError(t("commonConnectionErrorShort"));
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancel = async () => {
    if (!token) return;
    setShowCancelModal(false);
    setActionLoading("cancel");
    setError(null);
    try {
      const res = await fetch(
        `${API_URL}/public/reservations/${token}/cancel${q}`,
        {
          method: "POST",
        },
      );
      const data = await res.json();

      if (res.ok) {
        setReservation(data.reservation);
        setActionSuccess("cancel");
      } else {
        setError(data.message || t("manageCancelErrorFallback"));
      }
    } catch (err) {
      setError(t("commonConnectionErrorShort"));
    } finally {
      setActionLoading(null);
    }
  };

  // Open edit modal with current values. Los huecos reales (del día actual y
  // del mes del calendario) los piden los dos `useEffect` de abajo en cuanto
  // `showEditModal`/`editDate`/`editPartySize`/`currentMonth` cambian.
  const openEditModal = () => {
    if (reservation) {
      const initialDate = new Date(reservation.date);
      setEditDate(initialDate);
      setEditTime(reservation.time);
      setEditPartySize(reservation.partySize);
      setCurrentMonth(initialDate);
      setShowEditModal(true);
    }
  };

  // Huecos reales del día elegido para pedir un cambio: MISMO endpoint
  // público que usa `App.tsx` para la reserva nueva
  // (`GET /public/organizations/:slug/slots`), nunca una lista escrita a
  // mano. Se repite cada vez que cambia el día O el número de personas: la
  // disponibilidad depende de ambos.
  useEffect(() => {
    if (!showEditModal || !editDate || !reservation) return;
    const slug = reservation.location.slug;
    if (!slug) return;
    const controller = new AbortController();
    const dateStr = format(editDate, "yyyy-MM-dd");
    const originalDateStr = format(new Date(reservation.date), "yyyy-MM-dd");
    const isOriginalDate = dateStr === originalDateStr;
    const originalTime = reservation.time;

    const loadSlots = async () => {
      setSlotsLoading(true);
      setSlotsClosedReason(null);
      try {
        const partySizeParam =
          editPartySize > 0 ? `&partySize=${editPartySize}` : "";
        const res = await fetch(
          `${API_URL}/public/organizations/${slug}/slots?date=${dateStr}${partySizeParam}`,
          { signal: controller.signal },
        );
        const data = await res.json();
        if (!res.ok) {
          setAvailableSlots([]);
          return;
        }
        let slots: PublicTimeSlot[] = Array.isArray(data.slots)
          ? data.slots
          : [];
        // Este endpoint público no conoce la reserva, así que no puede
        // excluirla al calcular ocupación (a diferencia del PUT de guardado,
        // que sí la excluye — ver `.claude/memorias/frontend.notas.md`). Sin
        // este parche, la propia hora YA reservada por el cliente aparecería
        // "ocupada" al abrir el modal en su día actual.
        if (isOriginalDate) {
          const hasOriginal = slots.some((s) => s.time === originalTime);
          slots = hasOriginal
            ? slots.map((s) =>
                s.time === originalTime ? { ...s, available: true } : s,
              )
            : [...slots, { time: originalTime, available: true }].sort(
                (a, b) => a.time.localeCompare(b.time),
              );
        }
        setAvailableSlots(slots);
        // En el día actual de la reserva no bloqueamos con el aviso de
        // cierre: si el restaurante cerró ese día DESPUÉS de aceptar la
        // reserva, el cliente aún puede tocar solo el número de personas (el
        // backend ya no exige nada de calendario en ese caso).
        setSlotsClosedReason(
          !isOriginalDate && data.isClosed
            ? data.closedReason || t("appDefaultClosedReason")
            : null,
        );
      } catch (err) {
        if (controller.signal.aborted) return;
        setAvailableSlots([]);
      } finally {
        if (!controller.signal.aborted) setSlotsLoading(false);
      }
    };
    loadSlots();
    return () => controller.abort();
    // `t` se deja fuera a propósito (mismo criterio que `App.tsx`/`loadSlots`):
    // no es estable entre renders y no debe disparar un refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    showEditModal,
    editDate,
    editPartySize,
    reservation?.location.slug,
    reservation?.date,
    reservation?.time,
  ]);

  // Si la hora elegida deja de estar entre los huecos disponibles (cambio de
  // día, o de número de personas que ya no cabe), se limpia para no dejar
  // seleccionada una hora que el backend rechazaría.
  useEffect(() => {
    if (!editTime) return;
    const stillAvailable = availableSlots.some(
      (s) => s.time === editTime && s.available,
    );
    if (!stillAvailable) setEditTime("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableSlots]);

  // Disponibilidad por día del MES visible en el calendario del modal
  // (`GET /public/organizations/:slug/calendar`, mismo endpoint que usa
  // `App.tsx`) — para poder deshabilitar de entrada los días sin hueco, en
  // vez de dejar que el cliente los elija y se encuentre con una rejilla
  // vacía.
  useEffect(() => {
    if (!showEditModal || !reservation) return;
    const slug = reservation.location.slug;
    if (!slug) return;
    const controller = new AbortController();
    const loadCalendar = async () => {
      try {
        const startDate = format(startOfMonth(currentMonth), "yyyy-MM-dd");
        const endDate = format(endOfMonth(currentMonth), "yyyy-MM-dd");
        const partySizeParam =
          editPartySize > 0 ? `&partySize=${editPartySize}` : "";
        const res = await fetch(
          `${API_URL}/public/organizations/${slug}/calendar?startDate=${startDate}&endDate=${endDate}${partySizeParam}`,
          { signal: controller.signal },
        );
        const data = await res.json();
        if (res.ok && Array.isArray(data.days)) {
          setCalendarAvailability((prev) => {
            const next = { ...prev };
            (data.days as DayAvailabilityInfo[]).forEach((day) => {
              next[day.date] = day;
            });
            return next;
          });
        }
      } catch (err) {
        // Silencioso a propósito: sin datos de calendario, los días quedan
        // seleccionables como antes de esta feature — el aviso real llega
        // igualmente al elegir hora (`/slots`, efecto de arriba).
        if (controller.signal.aborted) return;
      }
    };
    loadCalendar();
    return () => controller.abort();
  }, [showEditModal, currentMonth, editPartySize, reservation?.location.slug]);

  // Handle modify reservation
  const handleModify = async () => {
    if (!token || !editDate || !editTime) return;
    // Bloqueo síncrono: si ya hay un envío en curso (doble-click/doble-tap
    // dentro del mismo ciclo de render), lo ignoramos sin tocar el estado.
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setEditLoading(true);
    setError(null);

    try {
      const res = await fetch(`${API_URL}/public/reservations/${token}${q}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          date: editDate.toISOString().split("T")[0],
          time: editTime,
          partySize: editPartySize,
        }),
      });
      const data = await res.json();

      if (res.ok) {
        // El backend devuelve la reserva con `pendingChangeRequest` ya
        // relleno (nueva solicitud o la existente si era idéntica), así que
        // el aviso de "solicitud pendiente" aparece de inmediato sin
        // necesidad de volver a pedir la reserva por separado.
        setReservation(data.reservation);
        setActionSuccess("modify");
        setShowEditModal(false);
      } else {
        setError(data.message || t("manageModifyErrorFallback"));
      }
    } catch (err) {
      setError(t("commonConnectionErrorShort"));
    } finally {
      setEditLoading(false);
      isSubmittingRef.current = false;
    }
  };

  // Calendar helpers
  const getDaysInMonth = (date: Date) => {
    const start = startOfMonth(date);
    const end = endOfMonth(date);
    return eachDayOfInterval({ start, end });
  };

  const isDateDisabled = (date: Date) => {
    if (isBefore(startOfDay(date), startOfDay(new Date()))) return true;
    if (!reservation) return false;
    const dateStr = format(date, "yyyy-MM-dd");
    // El día actual de la reserva siempre queda accesible (mismo criterio que
    // el parcheo de `/slots` de arriba): si el restaurante cerró ese día
    // después de aceptarla, el cliente aún puede tocar el número de personas.
    if (dateStr === format(new Date(reservation.date), "yyyy-MM-dd"))
      return false;
    const info = calendarAvailability[dateStr];
    return info ? !info.isOpen : false;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-teal-50 to-gray-100">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-teal-600 mx-auto mb-4" />
          <p className="text-gray-600">{t("manageLoading")}</p>
        </div>
      </div>
    );
  }

  if (error && !reservation) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-teal-50 to-gray-100 p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="flex justify-end mb-2">
            <LanguageSwitcher />
          </div>
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-800 mb-2">
            {t("manageNotFoundHeading")}
          </h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  if (!reservation) return null;

  const reservationDate = new Date(reservation.date);
  const formattedDate = formatDate(reservationDate, "long");
  const statusConfig = getStatusConfig(t);
  const baseStatus = statusConfig[reservation.status] || statusConfig.pending;
  const cancelledByEstablishment =
    reservation.status === "cancelled" &&
    reservation.cancelledBy === "establishment";
  const status = cancelledByEstablishment
    ? {
        ...baseStatus,
        label: t("statusCancelledByEstablishment"),
      }
    : baseStatus;
  const isFinalStatus = ["cancelled", "completed", "no_show"].includes(
    reservation.status,
  );
  // El cliente solo puede ratificar ("Confirmar asistencia") cuando el restaurante
  // ya ha aprobado la reserva. Si está en "pending" hay que esperar a la aprobación.
  const isPendingApproval = reservation.status === "pending";
  const canConfirm = reservation.status === "confirmed";
  const canCancel = !isFinalStatus;
  const hasPendingChangeRequest = !!reservation.pendingChangeRequest;
  const pendingChangeSummary = reservation.pendingChangeRequest
    ? formatPendingChangeSummary(reservation.pendingChangeRequest, t, formatDate)
    : "";

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-gray-100 p-4 flex items-center justify-center">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
        {/* Header with restaurant info */}
        <div className="bg-teal-600 text-white p-6 text-center relative">
          <div className="absolute top-3 right-3">
            <LanguageSwitcher variant="dark" />
          </div>
          {reservation.location.logo ? (
            <img
              src={reservation.location.logo}
              alt={reservation.location.name}
              className="w-20 h-20 rounded-full mx-auto mb-3 object-cover bg-white"
            />
          ) : (
            <div className="w-20 h-20 rounded-full mx-auto mb-3 bg-teal-500 flex items-center justify-center">
              <span className="text-3xl font-bold">
                {reservation.location.name.charAt(0)}
              </span>
            </div>
          )}
          <h1 className="text-xl font-bold">{reservation.location.name}</h1>
          {reservation.location.address && (
            <p className="text-teal-100 text-sm mt-1 flex items-center justify-center gap-1">
              <MapPin className="w-4 h-4" />
              {reservation.location.address}
            </p>
          )}
        </div>

        {/* Success message - Confirmación */}
        {actionSuccess === "confirm" && (
          <div className="p-6 bg-gradient-to-r from-green-50 to-emerald-50 border-b border-green-100">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <PartyPopper className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-lg font-bold text-green-800 mb-2">
                {t("manageConfirmSuccessHeading")}
              </h3>
              <p className="text-green-700 text-sm mb-4">
                {t("manageConfirmSuccessBody", {
                  date: formattedDate,
                  time: formatTime(reservation.time),
                })}
              </p>
              <a
                href={generateGoogleCalendarUrl(reservation, t, tPeople)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-white hover:bg-gray-50 text-gray-700 font-medium py-2 px-4 rounded-lg border border-gray-200 transition-colors text-sm"
              >
                <CalendarPlus className="w-4 h-4" />
                {t("manageAddToGoogleCalendar")}
              </a>
            </div>
          </div>
        )}

        {/* Success message - Cancelación */}
        {actionSuccess === "cancel" && (
          <div className="p-6 bg-gradient-to-r from-red-50 to-orange-50 border-b border-red-100">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="text-lg font-bold text-red-800 mb-2">
                {t("manageCancelSuccessHeading")}
              </h3>
              <p className="text-red-700 text-sm">
                {t("manageCancelSuccessBodyLine1")}
                <br />
                {t("manageCancelSuccessBodyLine2")}
              </p>
            </div>
          </div>
        )}

        {/* Success message - Modificación */}
        {actionSuccess === "modify" && (
          <div className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-blue-100">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Edit3 className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-lg font-bold text-blue-800 mb-2">
                {t("manageModifySuccessHeading")}
              </h3>
              <p className="text-blue-700 text-sm">
                {t("manageModifySuccessBodyLine1")}
                <br />
                {t("manageModifySuccessBodyLine2")}
              </p>
            </div>
          </div>
        )}

        {/* Reservation details */}
        <div className="p-6">
          {cancelledByEstablishment && (
            <div className="mb-5 p-4 bg-red-50 border border-red-200 rounded-xl">
              <div className="flex items-start gap-3">
                <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-red-800">
                    {t("manageCancelledByRestaurantTitle")}
                  </p>
                  <p className="text-sm text-red-700 mt-1">
                    {t("manageCancelledByRestaurantBody")}
                  </p>
                  {reservation.cancellationReason && (
                    <div className="mt-3 p-3 bg-white border border-red-100 rounded-lg">
                      <p className="text-xs uppercase tracking-wide text-red-600 font-semibold mb-1">
                        {t("manageCancellationReasonLabel")}
                      </p>
                      <p className="text-sm text-gray-800 whitespace-pre-wrap">
                        {reservation.cancellationReason}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-800">
              {t("manageYourReservationHeading")}
            </h2>
            <span
              className={`px-3 py-1 rounded-full text-sm font-medium ${status.bgColor} ${status.color}`}
            >
              {status.label}
            </span>
          </div>

          <div className="space-y-4">
            <div className="flex items-center gap-3 text-gray-700">
              <Calendar className="w-5 h-5 text-teal-600" />
              <span>{formattedDate}</span>
            </div>
            <div className="flex items-center gap-3 text-gray-700">
              <Clock className="w-5 h-5 text-teal-600" />
              <span>{formatTime(reservation.time)}</span>
            </div>
            <div className="flex items-center gap-3 text-gray-700">
              <Users className="w-5 h-5 text-teal-600" />
              <span>{tPeople(reservation.partySize)}</span>
            </div>
            {reservation.roomName && (
              <div className="flex items-center gap-3 text-gray-700">
                <MapPin className="w-5 h-5 text-teal-600" />
                <span>{t("manageRoomLabel", { roomName: reservation.roomName })}</span>
              </div>
            )}
          </div>

          <div className="mt-4 pt-4 border-t border-gray-100">
            <p className="text-sm text-gray-500">{t("manageCodeLabelBlock")}</p>
            <p className="font-mono font-bold text-gray-800">
              {reservation.confirmationCode}
            </p>
          </div>

          {reservation.notes && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <p className="text-sm text-gray-500">{t("manageNotesLabel")}</p>
              <p className="text-gray-700">{reservation.notes}</p>
            </div>
          )}

          {/* "Tu menú" (menús por fechas): solo aparece si hay selección
              guardada O menús disponibles para cambiar. */}
          {menusData &&
            (menusData.selections.length > 0 ||
              menusData.availableMenus.length > 0) && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <p className="text-sm text-gray-500 flex items-center gap-1.5">
                    <UtensilsCrossed className="w-4 h-4 text-teal-600" />
                    {t("manageMenuHeading")}
                  </p>
                  {menusData.editable && (
                    <button
                      type="button"
                      onClick={openMenuModal}
                      className="text-xs text-teal-600 hover:underline font-medium flex-shrink-0"
                    >
                      {t("manageMenuChangeButton")}
                    </button>
                  )}
                </div>
                {menusData.selections.length > 0 ? (
                  <ul className="text-gray-700 text-sm space-y-0.5">
                    {menusData.selections.map((sel) => (
                      <li key={sel.menuId}>
                        {t("commonMenuSummaryItem", {
                          qty: sel.quantity,
                          name: sel.name,
                        })}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-gray-500 text-sm">
                    {t("manageMenuNoneSelected")}
                  </p>
                )}
                {!menusData.editable && (
                  <p className="text-xs text-gray-400 mt-2">
                    {t("manageMenuNotEditableNotice")}
                  </p>
                )}
              </div>
            )}
        </div>

        {/* Actions */}
        {!isFinalStatus && (
          <div className="p-6 pt-0 space-y-3">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg text-sm flex items-start gap-2">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}
            {isPendingApproval && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-center text-sm text-amber-800">
                <strong>{t("managePendingApprovalBannerTitle")}</strong>
                <br />
                {t("managePendingApprovalBannerBody")}
              </div>
            )}
            {isPendingApproval ? (
              <button
                disabled
                title={t("manageConfirmDisabledTitle")}
                className="w-full bg-gray-200 text-gray-500 font-semibold py-3 px-4 rounded-xl cursor-not-allowed flex items-center justify-center gap-2"
              >
                <CheckCircle className="w-5 h-5" />
                {t("manageConfirmDisabledLabel")}
              </button>
            ) : (
              canConfirm && (
                <button
                  onClick={() => setShowConfirmModal(true)}
                  disabled={actionLoading !== null || editLoading}
                  className="w-full bg-teal-600 hover:bg-teal-700 text-white font-semibold py-3 px-4 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {actionLoading === "confirm" ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <CheckCircle className="w-5 h-5" />
                  )}
                  {t("manageConfirmButton")}
                </button>
              )
            )}

            {hasPendingChangeRequest && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-center text-sm text-blue-800">
                <strong>{t("managePendingChangeBannerTitle")}</strong>
                {pendingChangeSummary && (
                  <>
                    <br />
                    {pendingChangeSummary}
                  </>
                )}
                <br />
                {t("managePendingChangeBannerFooter")}
              </div>
            )}

            {/* Modify button */}
            {hasPendingChangeRequest ? (
              <button
                disabled
                title={t("managePendingChangeDisabledTitle")}
                className="w-full bg-gray-200 text-gray-500 font-semibold py-3 px-4 rounded-xl cursor-not-allowed flex items-center justify-center gap-2"
              >
                <Clock className="w-5 h-5" />
                {t("managePendingChangeDisabledLabel")}
              </button>
            ) : (
              <button
                onClick={openEditModal}
                disabled={actionLoading !== null || editLoading}
                className="w-full bg-white hover:bg-gray-50 text-blue-600 font-semibold py-3 px-4 rounded-xl border-2 border-blue-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <Edit3 className="w-5 h-5" />
                {t("manageModifyButton")}
              </button>
            )}

            {canCancel && (
              <button
                onClick={() => setShowCancelModal(true)}
                disabled={actionLoading !== null || editLoading}
                className="w-full bg-white hover:bg-gray-50 text-red-600 font-semibold py-3 px-4 rounded-xl border-2 border-red-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {actionLoading === "cancel" ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <XCircle className="w-5 h-5" />
                )}
                {t("manageCancelButton")}
              </button>
            )}
          </div>
        )}

        {/* Contact info */}
        {reservation.location.phone && (
          <div className="p-6 pt-0 space-y-3">
            {/* Llamar por teléfono */}
            <a
              href={`tel:${reservation.location.phone}`}
              className="flex items-center justify-center gap-2 text-teal-600 hover:text-teal-700 text-sm"
            >
              <Phone className="w-4 h-4" />
              {reservation.location.phone}
            </a>

            {/* Botón de WhatsApp */}
            <a
              href={`https://wa.me/${reservation.location.phone.replace(/[\s\-\(\)\+]/g, "").replace(/^(?!34)/, "34")}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white font-medium py-2.5 px-4 rounded-xl transition-colors text-sm"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
              </svg>
              {t("manageWhatsAppButton")}
            </a>
          </div>
        )}

        {/* Descarga de la app */}
        <div className="px-6 pb-2">
          <AppDownloadCta />
        </div>

        {/* Footer */}
        <div className="bg-gray-50 p-4 text-center">
          <p className="text-xs text-gray-500">
            {t("commonPoweredBy")}{" "}
            <a
              href={poweredByUrl(null, "gestion-reserva")}
              target="_blank"
              rel="noopener noreferrer"
              className="text-teal-600 hover:underline"
            >
              ReservaManager
            </a>
          </p>
        </div>
      </div>

      {/* Modal de Confirmar Asistencia */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden animate-in zoom-in-95 duration-200">
            {/* Header verde */}
            <div className="bg-gradient-to-r from-teal-500 to-emerald-500 p-6 text-white text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <CheckCircle className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold">{t("manageModalConfirmTitle")}</h3>
            </div>

            {/* Detalles de la reserva */}
            <div className="p-6">
              <div className="bg-gray-50 rounded-xl p-4 mb-4 space-y-2">
                <div className="flex items-center gap-2 text-gray-700">
                  <Calendar className="w-4 h-4 text-teal-600" />
                  <span className="text-sm">{formattedDate}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-700">
                  <Clock className="w-4 h-4 text-teal-600" />
                  <span className="text-sm">{formatTime(reservation.time)}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-700">
                  <Users className="w-4 h-4 text-teal-600" />
                  <span className="text-sm">{tPeople(reservation.partySize)}</span>
                </div>
              </div>

              <p className="text-gray-600 text-center text-sm mb-6">
                {(() => {
                  const [before, after] = tSplit(
                    "manageModalConfirmQuestion",
                    "name",
                  );
                  return (
                    <>
                      {before}
                      <strong>{reservation.location.name}</strong>
                      {after}
                    </>
                  );
                })()}
              </p>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowConfirmModal(false)}
                  className="flex-1 py-3 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-xl transition-colors"
                >
                  {t("manageModalConfirmBack")}
                </button>
                <button
                  onClick={handleConfirm}
                  className="flex-1 py-3 px-4 bg-teal-600 hover:bg-teal-700 text-white font-medium rounded-xl transition-colors flex items-center justify-center gap-2"
                >
                  <CheckCircle className="w-5 h-5" />
                  {t("manageModalConfirmSubmit")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Cancelar Reserva */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden animate-in zoom-in-95 duration-200">
            {/* Header rojo */}
            <div className="bg-gradient-to-r from-red-500 to-rose-500 p-6 text-white text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <XCircle className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold">{t("manageModalCancelTitle")}</h3>
            </div>

            {/* Detalles de la reserva */}
            <div className="p-6">
              <div className="bg-gray-50 rounded-xl p-4 mb-4 space-y-2">
                <div className="flex items-center gap-2 text-gray-700">
                  <Calendar className="w-4 h-4 text-red-500" />
                  <span className="text-sm">{formattedDate}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-700">
                  <Clock className="w-4 h-4 text-red-500" />
                  <span className="text-sm">{formatTime(reservation.time)}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-700">
                  <Users className="w-4 h-4 text-red-500" />
                  <span className="text-sm">{tPeople(reservation.partySize)}</span>
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
                <div className="flex items-start gap-2">
                  <Info className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-amber-800 text-sm">
                    <strong>{t("manageModalCancelWarningTitle")}</strong>{" "}
                    {(() => {
                      const [before, after] = tSplit(
                        "manageModalCancelWarningBody",
                        "name",
                      );
                      return (
                        <>
                          {before}
                          <strong>{reservation.location.name}</strong>
                          {after}
                        </>
                      );
                    })()}
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowCancelModal(false)}
                  className="flex-1 py-3 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-xl transition-colors"
                >
                  {t("manageModalCancelKeep")}
                </button>
                <button
                  onClick={handleCancel}
                  className="flex-1 py-3 px-4 bg-red-600 hover:bg-red-700 text-white font-medium rounded-xl transition-colors flex items-center justify-center gap-2"
                >
                  <XCircle className="w-5 h-5" />
                  {t("manageModalCancelSubmit")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Modificar Reserva */}
      {showEditModal && editDate && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in zoom-in-95 duration-200 my-4">
            {/* Header azul */}
            <div className="bg-gradient-to-r from-blue-500 to-indigo-500 p-6 text-white text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <Edit3 className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold">{t("manageModalEditTitle")}</h3>
            </div>

            <div className="p-6 space-y-6">
              {/* Selector de fecha - Calendario */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t("manageModalEditDateLabel")}
                </label>
                <div className="border border-gray-200 rounded-xl p-4">
                  {/* Navegación del mes */}
                  <div className="flex items-center justify-between mb-4">
                    <button
                      onClick={() =>
                        setCurrentMonth(subMonths(currentMonth, 1))
                      }
                      className="p-1 hover:bg-gray-100 rounded-lg"
                    >
                      <ChevronLeft className="w-5 h-5 text-gray-600" />
                    </button>
                    <span className="font-medium text-gray-800">
                      {formatDate(currentMonth, "monthYear")}
                    </span>
                    <button
                      onClick={() =>
                        setCurrentMonth(addMonths(currentMonth, 1))
                      }
                      className="p-1 hover:bg-gray-100 rounded-lg"
                    >
                      <ChevronRight className="w-5 h-5 text-gray-600" />
                    </button>
                  </div>

                  {/* Días de la semana */}
                  <div className="grid grid-cols-7 gap-1 mb-2">
                    {weekdays.map((day, idx) => (
                      <div
                        key={idx}
                        className="text-center text-xs font-medium text-gray-500 py-1"
                      >
                        {day}
                      </div>
                    ))}
                  </div>

                  {/* Días del mes */}
                  <div className="grid grid-cols-7 gap-1">
                    {(() => {
                      const days = getDaysInMonth(currentMonth);
                      const firstDayOfMonth = days[0].getDay();
                      const startPadding =
                        firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1;

                      return (
                        <>
                          {Array.from({ length: startPadding }).map((_, i) => (
                            <div key={`pad-${i}`} className="h-8" />
                          ))}
                          {days.map((day) => {
                            const isDisabled = isDateDisabled(day);
                            const isSelected =
                              editDate && isSameDay(day, editDate);
                            const isTodayDate = isToday(day);

                            return (
                              <button
                                key={day.toISOString()}
                                onClick={() => !isDisabled && setEditDate(day)}
                                disabled={isDisabled}
                                className={`
                                  h-8 w-full rounded-lg text-sm font-medium transition-colors
                                  ${isDisabled ? "text-gray-300 cursor-not-allowed" : "hover:bg-blue-50"}
                                  ${isSelected ? "bg-blue-600 text-white hover:bg-blue-700" : ""}
                                  ${isTodayDate && !isSelected ? "border border-blue-400 text-blue-600" : ""}
                                  ${!isSelected && !isDisabled && !isTodayDate ? "text-gray-700" : ""}
                                `}
                              >
                                {format(day, "d")}
                              </button>
                            );
                          })}
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>

              {/* Selector de hora: solo huecos REALES del día elegido, pedidos
                  al mismo endpoint que usa la reserva nueva (ver
                  `.claude/memorias/frontend.notas.md`, 18-sep-2026). */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t("manageModalEditTimeLabel")}
                </label>
                {slotsLoading ? (
                  <div className="flex justify-center py-4">
                    <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
                  </div>
                ) : (() => {
                    const openSlots = availableSlots.filter(
                      (slot) => slot.available,
                    );
                    if (openSlots.length === 0) {
                      return (
                        <div className="text-center py-4 text-sm text-gray-500 flex flex-col items-center gap-2 px-2">
                          <AlertCircle className="w-5 h-5 text-gray-400" />
                          <span>
                            {slotsClosedReason || t("appNoSlotsAvailable")}
                          </span>
                        </div>
                      );
                    }
                    return (
                      <div className="grid grid-cols-4 gap-2 max-h-32 overflow-y-auto">
                        {openSlots.map((slot) => (
                          <button
                            key={slot.time}
                            onClick={() => setEditTime(slot.time)}
                            className={`
                        py-2 px-3 rounded-lg text-sm font-medium transition-colors
                        ${
                          editTime === slot.time
                            ? "bg-blue-600 text-white"
                            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        }
                      `}
                          >
                            {slot.time}
                          </button>
                        ))}
                      </div>
                    );
                  })()}
              </div>

              {/* Selector de personas */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t("manageModalEditPartyLabel")}
                </label>
                <div className="flex items-center justify-center gap-4">
                  <button
                    onClick={() =>
                      setEditPartySize(Math.max(1, editPartySize - 1))
                    }
                    className="p-2 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                  >
                    <Minus className="w-5 h-5 text-gray-600" />
                  </button>
                  <span className="text-2xl font-bold text-gray-800 w-12 text-center">
                    {editPartySize}
                  </span>
                  <button
                    onClick={() => setEditPartySize(editPartySize + 1)}
                    className="p-2 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                  >
                    <Plus className="w-5 h-5 text-gray-600" />
                  </button>
                </div>
              </div>

              {/* Resumen de cambios: sin hora elegida (día sin huecos, o aún
                  sin tocar) no hay nada que resumir todavía. */}
              {editTime && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <p className="text-blue-800 text-sm text-center">
                    {t("manageModalEditSummaryPrefix")}{" "}
                    <strong>{formatDate(editDate, "dayMonth")}</strong>{" "}
                    {t("manageModalEditSummaryAt")}{" "}
                    <strong>{editTime}</strong>{" "}
                    {t("manageModalEditSummaryFor")}{" "}
                    <strong>{tPeople(editPartySize)}</strong>
                  </p>
                </div>
              )}

              {/* Botones */}
              <div className="flex gap-3">
                <button
                  onClick={() => setShowEditModal(false)}
                  disabled={editLoading}
                  className="flex-1 py-3 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-xl transition-colors disabled:opacity-50"
                >
                  {t("manageModalEditCancel")}
                </button>
                <button
                  onClick={handleModify}
                  disabled={editLoading || !editDate || !editTime}
                  className="flex-1 py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-xl transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {editLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <CheckCircle className="w-5 h-5" />
                  )}
                  {t("manageModalEditSubmit")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Cambiar Menú */}
      {showMenuModal && menusData && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in zoom-in-95 duration-200 my-4">
            {/* Header teal */}
            <div className="bg-gradient-to-r from-teal-500 to-emerald-500 p-6 text-white text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <UtensilsCrossed className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold">{t("manageMenuModalTitle")}</h3>
            </div>

            <div className="p-6 space-y-4">
              <div className="space-y-3">
                {menusData.availableMenus.map((menu) => {
                  const quantity = menuModalQuantities[menu.id] || 0;
                  const isExpanded = menuModalExpandedId === menu.id;
                  return (
                    <div
                      key={menu.id}
                      className="border border-gray-200 rounded-lg p-3"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="font-medium text-gray-800 truncate">
                            {menu.name}
                          </p>
                          <p className="text-sm text-gray-500">
                            {menu.price.toFixed(2)} €
                          </p>
                        </div>
                      </div>

                      {menu.description && (
                        <button
                          type="button"
                          onClick={() =>
                            setMenuModalExpandedId(
                              isExpanded ? null : menu.id,
                            )
                          }
                          className="mt-1 flex items-center gap-1 text-xs text-teal-600 hover:underline"
                        >
                          {isExpanded ? (
                            <>
                              {t("commonMenuHideDescription")}
                              <ChevronUp className="w-3 h-3" />
                            </>
                          ) : (
                            <>
                              {t("commonMenuShowDescription")}
                              <ChevronDown className="w-3 h-3" />
                            </>
                          )}
                        </button>
                      )}
                      {isExpanded && menu.description && (
                        <p className="mt-2 text-sm text-gray-600 whitespace-pre-line">
                          {menu.description}
                        </p>
                      )}

                      <div className="mt-3">
                        {menu.scope === "all_guests" ? (
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={
                                quantity === reservation.partySize &&
                                quantity > 0
                              }
                              onChange={(e) =>
                                setAllGuestsMenuModalQuantity(
                                  menu.id,
                                  e.target.checked,
                                )
                              }
                              className="w-4 h-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                            />
                            <span className="text-sm text-gray-700">
                              {t("commonMenuAllGuestsLabel", {
                                count: reservation.partySize,
                              })}
                            </span>
                          </label>
                        ) : (
                          <div className="flex items-center gap-3">
                            <button
                              type="button"
                              onClick={() =>
                                decrementMenuModalQuantity(menu.id)
                              }
                              disabled={quantity <= 0}
                              className="w-8 h-8 rounded-full bg-gray-100 text-gray-700 flex items-center justify-center hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              <Minus className="w-4 h-4" />
                            </button>
                            <span className="w-6 text-center font-semibold text-gray-800">
                              {quantity}
                            </span>
                            <button
                              type="button"
                              onClick={() =>
                                incrementMenuModalQuantity(menu.id)
                              }
                              disabled={menuModalTotal >= reservation.partySize}
                              className="w-8 h-8 rounded-full bg-gray-100 text-gray-700 flex items-center justify-center hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              <Plus className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              <p className="text-sm text-gray-500">
                {t("commonMenuTotalLabel", {
                  total: menuModalTotal,
                  count: reservation.partySize,
                })}
              </p>

              {menuModalExceedsPartySize && (
                <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">
                  {t("commonMenuExceedsPartySize")}
                </div>
              )}

              {menuModalError && (
                <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg text-sm flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>{menuModalError}</span>
                </div>
              )}

              {/* Botones */}
              <div className="flex gap-3">
                <button
                  onClick={() => setShowMenuModal(false)}
                  disabled={menuModalSaving}
                  className="flex-1 py-3 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-xl transition-colors disabled:opacity-50"
                >
                  {t("manageModalEditCancel")}
                </button>
                <button
                  onClick={handleSaveMenu}
                  disabled={menuModalSaving || menuModalExceedsPartySize}
                  className="flex-1 py-3 px-4 bg-teal-600 hover:bg-teal-700 text-white font-medium rounded-xl transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {menuModalSaving ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <CheckCircle className="w-5 h-5" />
                  )}
                  {t("manageModalEditSubmit")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
